import { Flex, Switch, Text, useToast } from "@chakra-ui/react";
import { Fragment, useCallback, useEffect, useMemo, useRef } from "react";
import { IoGitPullRequestOutline } from "react-icons/io5";
import { TbReportSearch } from "react-icons/tb";
import { VscRunAll } from "react-icons/vsc";
import { FormattedMessage, useIntl } from "react-intl";

import { MenuAction, isHrefAction } from "@nens/ranalib";
import { useLocalStorage } from "@nens/react/hooks";
import { capitalizeFirst } from "@nens/shared-utils";

import { StatusBanner } from "@/components/status-banner";
import { E2EStatus, e2eStatusToAlertStatus, useE2EStatusQuery } from "@/queries/query-e2e-status";
import { getPrettyTimeFromNow } from "@/utils/get-pretty-time";

const ActionsAsLinks = ({ actions }: { actions: MenuAction[] }) => {
  return (
    <Flex>
      {actions.map((menuAction, idx) => {
        const { label } = menuAction;
        const href = isHrefAction(menuAction) ? menuAction.href : undefined;
        const action = "action" in menuAction ? menuAction.action : undefined;
        const isNotFirst = idx !== 0;
        const isLast = idx === actions.length - 1;
        return (
          <Fragment key={label}>
            {isNotFirst && (
              <Text as="span" ml={isLast ? 1 : 0} mr={1}>
                {isLast ? "or" : ","}
              </Text>
            )}
            <Text
              as={href ? "a" : undefined}
              href={href}
              target={href ? "_blank" : undefined}
              rel={href ? "noreferrer" : undefined}
              cursor="pointer"
              onClick={action}
              textDecor="underline"
            >
              {label}
            </Text>
          </Fragment>
        );
      })}
    </Flex>
  );
};

const useActions = (e2eStatus: E2EStatus | undefined): MenuAction[] => {
  const intl = useIntl();

  return useMemo(() => {
    if (!e2eStatus) return [];

    const actions: MenuAction[] = [
      {
        label: intl.formatMessage({ id: "e2e_status_view_run", defaultMessage: "View run" }),
        href: e2eStatus.runUrl,
        icon: <VscRunAll />,
      },
    ];

    if (e2eStatus.status === "success" || e2eStatus.status === "failure") {
      actions.push({
        label: intl.formatMessage({ id: "e2e.see_report", defaultMessage: "See report" }),
        href: `https://cicd.ranawaterintelligence.com/playwright/gitops/${e2eStatus.runId}/`,
        icon: <TbReportSearch />,
      });
    }

    if (e2eStatus.status === "success") {
      actions.push({
        label: intl.formatMessage({
          id: "e2e.see_pull_requests",
          defaultMessage: "See pull requests",
        }),
        href: "https://github.com/nens/rana-gitops/pulls",
        icon: <IoGitPullRequestOutline />,
      });
    }

    return actions;
  }, [e2eStatus, intl]);
};

export const E2EStatusBanner = () => {
  const toast = useToast();
  const intl = useIntl();
  const [allowToast, setAllowToast] = useLocalStorage("allow_e2e_toast", false);
  const [isInProgress, setIsInProgress] = useLocalStorage("e2e_is_in_progress", false);
  const lastNotifiedRunId = useRef<number | null>(null);

  const { data: e2eStatus } = useE2EStatusQuery();
  const actions = useActions(e2eStatus);

  const checkAndApplyToast = useCallback(() => {
    if (!e2eStatus || !allowToast) return;
    // Only show toast when status changes to error/success/warning, not when it changes to inProgress or when the component first mounts with a non-inProgress status.
    const alertStatus = e2eStatusToAlertStatus(e2eStatus.status);
    const isCurrentlyInProgress = alertStatus === "inProgress";
    if (isCurrentlyInProgress) {
      setIsInProgress(true);
      lastNotifiedRunId.current = null;
      return;
    }

    if (!isCurrentlyInProgress) {
      if (document.hidden || !document.hasFocus()) return; // Don't show toast if user is not looking at the page (re-checked on window focus).
      setTimeout(() => setIsInProgress(false)); // Set to false in next tick so it doesn't interfere with the toast.
      if (!isInProgress) {
        return;
      }
    }

    // Make sure we only show the toast once per runId, even if the component re-renders multiple times with the same e2eStatus.
    if (lastNotifiedRunId.current === e2eStatus.runId) {
      return;
    }
    lastNotifiedRunId.current = e2eStatus.runId;

    if (alertStatus === "error") {
      toast({
        title: intl.formatMessage({
          id: "e2e_status_failed",
          defaultMessage: "E2E tests failed 🚨",
        }),
        description: <ActionsAsLinks actions={actions} />,
        status: "error",
        isClosable: true,
        id: "e2e_failed_toast",
      });
    } else if (alertStatus === "success") {
      toast({
        title: intl.formatMessage({
          id: "e2e_status_succeeded",
          defaultMessage: "E2E tests succeeded 🎉",
        }),
        description: <ActionsAsLinks actions={actions} />,
        status: "success",
        isClosable: true,
        id: "e2e_success_toast",
      });
    } else if (alertStatus === "warning") {
      toast({
        title: intl.formatMessage(
          {
            id: "e2e_status_warning",
            defaultMessage: "E2E tests run has a warning status - {status} ❓",
          },
          { status: e2eStatus.status }
        ),
        description: <ActionsAsLinks actions={actions} />,
        status: "warning",
        isClosable: true,
        id: "e2e_warning_toast",
      });
    }
  }, [e2eStatus, allowToast, setIsInProgress, isInProgress, toast, intl, actions]);

  useEffect(() => {
    checkAndApplyToast();

    document.addEventListener("visibilitychange", checkAndApplyToast);
    window.addEventListener("focus", checkAndApplyToast);
    return () => {
      document.removeEventListener("visibilitychange", checkAndApplyToast);
      window.removeEventListener("focus", checkAndApplyToast);
    };
  }, [checkAndApplyToast]);

  if (!e2eStatus) return null;

  const { status, summary, datetime } = e2eStatus;
  const alertStatus = e2eStatusToAlertStatus(status);

  return (
    <StatusBanner
      status={alertStatus}
      title={
        <>
          <FormattedMessage id="e2e_status" defaultMessage="E2E Status" /> - {status}
        </>
      }
      description={
        <Text maxW="xs" whiteSpace="pre-wrap">
          {summary}
        </Text>
      }
      timestamp={capitalizeFirst(getPrettyTimeFromNow(datetime))}
      actions={actions}
      ariaLabel="Open e2e status"
      footer={
        <Switch
          size="sm"
          isChecked={allowToast}
          onChange={(e) => {
            const isChecked = e.target.checked;
            setAllowToast(isChecked);
            if (!isChecked) setIsInProgress(false);
          }}
        >
          <FormattedMessage id="e2e_status.allow_toast" defaultMessage="Allow notifications" />
        </Switch>
      }
    />
  );
};
